/**
 * Resilient React.lazy() for Vite code-split routes.
 *
 * After a deploy, long-lived tabs or cached entry bundles can reference stale
 * chunk hashes (404). Retries the dynamic import, then reloads once so the browser
 * picks up the current index.html + asset manifest.
 */

import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

export const CHUNK_RELOAD_SESSION_KEY = 'tracekit-chunk-reload-at';

const CHUNK_ERROR_PATTERNS = [
  'Failed to fetch dynamically imported module',
  'Importing a module script failed',
  'Loading chunk',
  'Loading CSS chunk',
  'error loading dynamically imported module',
] as const;

export function isChunkLoadError(error: unknown): boolean {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : String(error ?? '');

  if (error instanceof Error && error.name === 'ChunkLoadError') {
    return true;
  }

  const normalized = message.toLowerCase();
  return CHUNK_ERROR_PATTERNS.some((pattern) => normalized.includes(pattern.toLowerCase()));
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/**
 * Reload the page once per session window when a stale chunk is detected.
 * Throws a user-facing error if a reload was already attempted recently.
 */
export function reloadOnceForStaleChunks(): never {
  const now = Date.now();
  const lastReloadRaw = sessionStorage.getItem(CHUNK_RELOAD_SESSION_KEY);
  const lastReload = lastReloadRaw ? Number(lastReloadRaw) : 0;

  if (!lastReload || now - lastReload > 10_000) {
    sessionStorage.setItem(CHUNK_RELOAD_SESSION_KEY, String(now));
    window.location.reload();
  }

  throw new Error(
    'GovEarner was updated while this tab was open. Hard-refresh the page (Ctrl+Shift+R or Cmd+Shift+R).',
  );
}

export type LazyModuleDefault<T extends ComponentType<unknown>> = { default: T };

export function lazyWithRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<LazyModuleDefault<T>>,
  retries = 2,
): LazyExoticComponent<T> {
  return lazy(async () => {
    let lastError: unknown;

    for (let attempt = 0; attempt <= retries; attempt += 1) {
      try {
        return await factory();
      } catch (error) {
        lastError = error;
        if (!isChunkLoadError(error)) {
          throw error;
        }
        if (attempt < retries) {
          await sleep(400 * (attempt + 1));
          continue;
        }
      }
    }

    reloadOnceForStaleChunks();
    throw lastError instanceof Error ? lastError : new Error(String(lastError));
  });
}

/**
 * Prefetch a lazy route chunk during idle time (after auth / shell mount).
 */
export function prefetchLazyRoute(factory: () => Promise<unknown>): void {
  const run = () => {
    void factory().catch(() => {
      // Prefetch is best-effort; lazyWithRetry handles failures at navigation time.
    });
  };

  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(run, { timeout: 3000 });
  } else {
    window.setTimeout(run, 250);
  }
}

/**
 * Global safety net for chunk load failures outside React.lazy (e.g. nested splits).
 */
export function registerChunkLoadRecovery(): void {
  if (typeof window === 'undefined') {
    return;
  }

  window.addEventListener('unhandledrejection', (event) => {
    if (!isChunkLoadError(event.reason)) {
      return;
    }
    event.preventDefault();
    reloadOnceForStaleChunks();
  });

  window.addEventListener('error', (event) => {
    if (!isChunkLoadError(event.error ?? event.message)) {
      return;
    }
    event.preventDefault();
    reloadOnceForStaleChunks();
  });
}
